import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, DataTable, DefinitionList, Drawer, Inline, PageHeader, Skeleton, Stack, Tabs, TabPanel, Tag, t, Text } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { RefusalPage } from '../../components/Refusal';
import { api, unwrap } from '../../lib/api';
import { shortDate } from '../../lib/format';
import './infrastructure.css';
import { FULL_HEIGHT } from '../../lib/tables';

type Map = maintenance.paths['/infrastructure']['get']['responses']['200']['content']['application/json'];
type Layer = Map['nodes'][number]['rows'][number]['layer'] | 'planned';

const LAYERS: Layer[] = ['resources', 'network', 'flows', 'filtering', 'rights', 'secrets', 'planned'];
const STORAGE = 'kanbios-infrastructure-layers';

function readLayers(): Record<Layer, boolean> {
  const all = Object.fromEntries(LAYERS.map(l => [l, true])) as Record<Layer, boolean>;
  try { return { ...all, ...JSON.parse(localStorage.getItem(STORAGE) ?? '{}') }; } catch { return all; }
}

/**
 * « Infrastructure » (D36): the platform as its code describes it, by layers
 * (network, flows, filtering, rights, secrets, the D35 target). Administrators
 * only; the content comes from GET /infrastructure. Like the « Carte du
 * projet », the map is drawn outside the DSM with the DSM tokens.
 */
export function InfrastructurePage() {
  const query = useQuery({ queryKey: ['infrastructure'], queryFn: async () => unwrap(await api.maintenance.GET('/infrastructure')), staleTime: 10 * 60_000 });
  const [layers, setLayers] = useState(readLayers);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState('acl');

  useEffect(() => { try { localStorage.setItem(STORAGE, JSON.stringify(layers)); } catch { /* private window: the choice lasts the visit */ } }, [layers]);

  const crumbs = [{ label: t('nav.groups.project') }, { label: t('infra.title') }];
  if (query.error) return <Shell crumbs={crumbs}><RefusalPage error={query.error} /></Shell>;
  const map = query.data;
  const off = LAYERS.filter(l => !layers[l]).map(l => `off-${l}`).join(' ');
  const detail = map?.details.find(d => d.id === selected) ?? null;
  // D58: « D35 » (planned) in its own column, never a badge after the name.
  const planned = (row: { planned: boolean }) => (row.planned ? <Badge tone="brand" variant="ring">{t('infra.d35')}</Badge> : null);
  const plannedColumn = { key: 'planned', label: t('infra.col.planned'), width: 90, accessor: (r: { planned: boolean }) => (r.planned ? t('infra.d35') : ''), render: planned };

  return (
    <Shell crumbs={crumbs}>
      <Stack gap={16} className="ix-page">
        <PageHeader
          title={t('infra.title')}
          subtitle={t('infra.subtitle')}
          actions={map && <Text variant="body-sm" tone="mute">{t('infra.asOf', { date: shortDate(map.asOf) })}</Text>}
        />
        <Inline gap={8} wrap align="center">
          <Text variant="body-sm" tone="mute">{t('infra.layers')}</Text>
          {LAYERS.map(l => (
            <Tag key={l} selected={layers[l]} color={`var(--ix-${l})`} onClick={() => setLayers(s => ({ ...s, [l]: !s[l] }))}>{t(`infra.layer.${l}`)}</Tag>
          ))}
          <Tag onClick={() => setLayers(Object.fromEntries(LAYERS.map(l => [l, true])) as Record<Layer, boolean>)}>{t('infra.showAll')}</Tag>
        </Inline>

        <section className={`ix-map ${off}`} aria-label={t('infra.map')}>
          {!map ? <Skeleton height={480} /> : (
            <div className="ix-scroll">
              <MapSvg map={map} selected={selected} onSelect={setSelected} />
            </div>
          )}
          <div className="ix-legend">
            <span><span className="ix-key live" />{t('infra.legend.live')}</span>
            <span><span className="ix-key planned" />{t('infra.legend.planned')}</span>
            <span><span className="ix-key future" />{t('infra.legend.future')}</span>
            <span>{t('infra.legend.click')}</span>
          </div>
        </section>

        {map && (
          <section className={`ix-tables ${off}`}>
            <Tabs id="ix-tabs" value={tab} onChange={setTab} label={t('infra.tables')}
              items={[{ id: 'acl', label: t('infra.tab.acl') }, { id: 'identities', label: t('infra.tab.identities') }, { id: 'secrets', label: t('infra.tab.secrets') }, { id: 'flows', label: t('infra.tab.flows') }]} />
            <TabPanel id="acl" tabsId="ix-tabs">
              <Stack gap={8}>
                <Text tone="mute">{t('infra.aclIntro')}</Text>
                <DataTable height={FULL_HEIGHT} label={t('infra.tab.acl')} rowKey="position" rows={map.acl} density="compact" toolbar={false} columns={[
                  { key: 'position', label: '#', width: 56, mono: true },
                  { key: 'action', label: t('infra.col.action'), width: 110, render: r => <Badge tone={r.action === 'accept' ? 'pos' : 'neg'}>{t(`infra.action.${r.action}`)}</Badge> },
                  { key: 'protocol', label: t('infra.col.protocol'), width: 80, mono: true },
                  { key: 'source', label: t('infra.col.source'), width: 190, mono: true },
                  { key: 'destination', label: t('infra.col.destination'), width: 240, mono: true },
                  { key: 'role', label: t('infra.col.role'), width: 200, grow: true },
                ]} />
              </Stack>
            </TabPanel>
            {/* One line per row: the long sentences truncate, their cell tooltip gives the whole text. */}
            <TabPanel id="identities" tabsId="ix-tabs">
              <DataTable height={FULL_HEIGHT} label={t('infra.tab.identities')} rowKey="id" rows={map.identities.filter(r => layers.planned || !r.planned)} density="compact" toolbar={false} columns={[
                { key: 'name', label: t('infra.col.identity'), width: 320, render: r => <Text variant="body-sm" strong truncate>{r.name}</Text> },
                plannedColumn,
                { key: 'rights', label: t('infra.col.rights'), width: 240, grow: true },
                { key: 'key', label: t('infra.col.key'), width: 240 },
                { key: 'usedBy', label: t('infra.col.usedBy'), width: 240 },
              ]} />
            </TabPanel>
            <TabPanel id="secrets" tabsId="ix-tabs">
              <DataTable height={FULL_HEIGHT} label={t('infra.tab.secrets')} rowKey="id" rows={map.secrets.filter(r => layers.planned || !r.planned)} density="compact" toolbar={false} columns={[
                { key: 'name', label: t('infra.col.secret'), width: 320, render: r => <Text variant="code" truncate>{r.name}</Text> },
                plannedColumn,
                { key: 'store', label: t('infra.col.store'), width: 260 },
                { key: 'readBy', label: t('infra.col.readBy'), width: 180 },
                { key: 'content', label: t('infra.col.content'), width: 240, grow: true },
              ]} />
            </TabPanel>
            <TabPanel id="flows" tabsId="ix-tabs">
              <DataTable height={FULL_HEIGHT} label={t('infra.tab.flows')} rowKey="id" rows={map.flows.filter(r => layers.planned || !r.planned)} density="compact" toolbar={false} columns={[
                { key: 'name', label: t('infra.col.flow'), width: 240, render: r => <Text variant="body-sm" strong truncate>{r.name}</Text> },
                plannedColumn,
                { key: 'path', label: t('infra.col.path'), width: 300 },
                { key: 'port', label: t('infra.col.port'), width: 110, mono: true },
                { key: 'allowedBy', label: t('infra.col.allowedBy'), width: 240, grow: true },
              ]} />
            </TabPanel>
          </section>
        )}
      </Stack>

      <Drawer open={Boolean(detail)} onClose={() => setSelected(null)} size="xl" eyebrow={detail?.kind} title={detail?.title ?? ''}>
        {detail && (
          <Stack gap={16}>
            <DefinitionList stacked items={detail.items.map((i, n) => ({ key: String(n), label: i.label, value: i.text }))} />
            <Text variant="body-sm" tone="mute">{t('infra.source')} : <span className="ix-mono">{detail.source}</span></Text>
          </Stack>
        )}
      </Drawer>
    </Shell>
  );
}

function MapSvg({ map, selected, onSelect }: { map: Map; selected: string | null; onSelect: (id: string) => void }) {
  const kind = (layer: string, isPlanned: boolean) => (isPlanned ? 'planned' : layer);
  return (
    <svg className="ix-svg" viewBox={`0 -6 ${map.size.width} ${map.size.height}`} role="group" aria-label={t('infra.map')}>
      <defs>
        {['flows', 'network', 'rights', 'planned'].map(k => (
          <marker key={k} id={`ix-arrow-${k}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className={`ix-arrow ${k}`} />
          </marker>
        ))}
      </defs>
      {map.zones.map(z => (
        <g key={z.id} className={`${z.kind === 'vpc' ? 'l-network' : ''} ${z.planned ? 'l-planned' : ''}`}>
          <rect className={`ix-zone ${z.kind}${z.planned ? ' planned' : ''}`} x={z.x} y={z.y} width={z.w} height={z.h} rx={z.kind === 'org' ? 18 : 14} />
          <text className="ix-zone-title" x={z.x + 16} y={z.y + 22}>{z.title}</text>
          <text className="ix-zone-sub" x={z.x + 16} y={z.y + 38}>{z.sub}</text>
        </g>
      ))}
      <g className="l-network">
        {map.bands.map(b => (
          <g key={b.id}>
            <rect className="ix-band" x={362} y={b.y} width={736} height={b.h} rx={10} />
            <text className="ix-band-name" x={374} y={b.y + 22}>{b.name}</text>
            <text className="ix-band-cidr" x={374} y={b.y + 40}>{b.cidr}</text>
          </g>
        ))}
      </g>
      <g className="l-filtering ix-acl" role="button" tabIndex={0} onClick={() => onSelect('acl')}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect('acl'); } }}>
        <title>{t('infra.aclOpen')}</title>
        <rect x={map.aclPanel.x} y={map.aclPanel.y} width={map.aclPanel.w} height={map.aclPanel.h} rx={10} />
        <foreignObject x={map.aclPanel.x} y={map.aclPanel.y} width={map.aclPanel.w} height={map.aclPanel.h}>
          <div className="ix-acl-text">{map.aclPanel.lines.map((l, i) => (l ? <p key={i} className={l.strong ? 'strong' : ''}>{l.text}</p> : <br key={i} />))}</div>
        </foreignObject>
      </g>
      <g>
        {map.edges.map(e => {
          const k = kind(e.layer, e.planned);
          return <path key={e.id} className={`ix-edge ${k} l-${e.layer}${e.planned ? ' l-planned' : ''}`} d={e.points.map((p, i) => `${i ? 'L' : 'M'}${p[0]},${p[1]}`).join(' ')} markerEnd={`url(#ix-arrow-${k})`} />;
        })}
      </g>
      <g>
        {map.nodes.map(n => (
          <foreignObject key={n.id} x={n.x} y={n.y} width={n.w} height={n.h} className={n.status === 'planned' ? 'l-planned' : ''}>
            <div className={`ix-node ${n.status}${selected === n.id ? ' selected' : ''}`} role="button" tabIndex={0}
              aria-label={t('infra.nodeOpen', { name: n.title })}
              onClick={() => onSelect(n.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(n.id); } }}>
              <div className="ix-title">{n.title}{n.tag && <span className="ix-tag">{n.tag}</span>}</div>
              {n.sub && <div className="l-resources">{n.sub}</div>}
              {n.mono && <div className="ix-mono l-resources">{n.mono}</div>}
              {n.rows.map((r, i) => (
                <div key={i} className={`ix-row ${r.layer} l-${r.layer}${r.planned ? ' planned l-planned' : ''}`}><b>{r.label}</b><span>{r.text}</span></div>
              ))}
            </div>
          </foreignObject>
        ))}
      </g>
      <g>{map.edges.filter(e => e.label).map(e => <EdgeLabel key={e.id} className={`${kind(e.layer, e.planned)} l-${e.layer}${e.planned ? ' l-planned' : ''}`} x={e.label!.x} y={e.label!.y} text={e.label!.text} />)}</g>
    </svg>
  );
}

/** A flow's label on its own background, sized to its text. */
function EdgeLabel({ x, y, text, className }: { x: number; y: number; text: string; className: string }) {
  const ref = useRef<SVGTextElement>(null);
  const [box, setBox] = useState<DOMRect | null>(null);
  useLayoutEffect(() => { if (ref.current) setBox(ref.current.getBBox()); }, [text]);
  return (
    <g className={`ix-elabel ${className}`}>
      {box && <rect x={box.x - 6} y={box.y - 3} width={box.width + 12} height={box.height + 6} rx={5} />}
      <text ref={ref} x={x} y={y + 4} textAnchor="middle">{text}</text>
    </g>
  );
}
